import { useEffect } from "react";

/**
 * A tiny "open the Display settings" channel. The one-time density notice
 * lives outside the React tree (a toast), while the setting lives in the
 * account menu (`NavUser` > `DensityRow`), so the two meet over a window event.
 */
const DISPLAY_SETTINGS_EVENT = "caseblink:open-display-settings";

type RequestDetail = { handled: boolean };

/**
 * Ask the account menu to open with its Display submenu expanded.
 * Returns `false` when nothing is listening (no account menu is mounted).
 */
export function requestDisplaySettings(): boolean {
  const detail: RequestDetail = { handled: false };
  window.dispatchEvent(new CustomEvent(DISPLAY_SETTINGS_EVENT, { detail }));
  return detail.handled;
}

/** Run `onRequest` whenever something asks for the Display settings. */
export function useDisplaySettingsRequest(onRequest: () => void): void {
  useEffect(() => {
    const listener = (event: Event) => {
      const { detail } = event as CustomEvent<RequestDetail>;
      if (detail) detail.handled = true;
      onRequest();
    };
    window.addEventListener(DISPLAY_SETTINGS_EVENT, listener);
    return () => window.removeEventListener(DISPLAY_SETTINGS_EVENT, listener);
  }, [onRequest]);
}
